import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Mail, Film, ArrowLeft, Lock, CheckCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

const ForgotPassword = () => {
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [resetToken, setResetToken] = useState('')
  const { register, handleSubmit, formState: { errors }, watch } = useForm()
  const { sendEmail } = useAuth()

  const password = watch('newPassword')

  const hashPassword = (password) => {
    let hash = 0
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return hash.toString(36)
  }

  const onSubmitEmail = (data) => {
    const admins = JSON.parse(localStorage.getItem('shikdar_films_admins') || '[]')
    const defaultAdmin = { username: 'admin', email: 'admin@shikdarfilms.com' }
    
    const allAdmins = [defaultAdmin, ...admins]
    const admin = allAdmins.find(a => a.email === data.email)

    if (!admin) {
      toast.error('No account found with this email address')
      return
    }

    const token = Math.random().toString(36).substring(2, 15)
    setResetToken(token)
    setEmail(data.email)

    const resetTokens = JSON.parse(localStorage.getItem('shikdar_films_reset_tokens') || '[]')
    resetTokens.push({
      email: data.email,
      token: token,
      timestamp: new Date().toISOString(),
      used: false
    })
    localStorage.setItem('shikdar_films_reset_tokens', JSON.stringify(resetTokens))

    // Send password reset email
    sendEmail({
      to: data.email,
      subject: 'Password Reset Request - Shikdar Films Admin',
      type: 'password_reset',
      body: `Dear Admin,\n\nYou have requested to reset your password.\n\nYour verification code is: ${token}\n\nThis code will expire in 24 hours.\n\nIf you did not request this, please ignore this email.\n\nBest regards,\nShikdar Films Team`
    })

    toast.success('Reset code sent! Check your email (simulated)')
    setStep('verify')
  }

  const onSubmitVerify = (data) => {
    const resetTokens = JSON.parse(localStorage.getItem('shikdar_films_reset_tokens') || '[]')
    const validToken = resetTokens.find(t => 
      t.email === email && 
      t.token === data.verificationCode && 
      !t.used
    )

    if (!validToken) {
      toast.error('Invalid or expired verification code')
      return
    }

    toast.success('Code verified! Please set your new password')
    setStep('reset')
  }

  const onSubmitReset = (data) => {
    const admins = JSON.parse(localStorage.getItem('shikdar_films_admins') || '[]')
    
    if (email === 'admin@shikdarfilms.com') {
      localStorage.setItem('shikdar_films_default_admin_password', hashPassword(data.newPassword))
    } else {
      const updatedAdmins = admins.map(admin => 
        admin.email === email 
          ? { ...admin, password: hashPassword(data.newPassword) }
          : admin
      )
      localStorage.setItem('shikdar_films_admins', JSON.stringify(updatedAdmins))
    }

    const resetTokens = JSON.parse(localStorage.getItem('shikdar_films_reset_tokens') || '[]')
    const updatedTokens = resetTokens.map(t => 
      t.email === email && t.token === resetToken
        ? { ...t, used: true }
        : t
    )
    localStorage.setItem('shikdar_films_reset_tokens', JSON.stringify(updatedTokens))

    // Send confirmation email
    sendEmail({
      to: email,
      subject: 'Password Reset Successful - Shikdar Films Admin',
      type: 'password_reset_confirmation',
      body: `Dear Admin,\n\nYour password has been successfully reset.\n\nYou can now log in with your new password at: ${window.location.origin}/admin/login\n\nIf you did not perform this action, please contact support immediately.\n\nBest regards,\nShikdar Films Team`
    })

    const activities = JSON.parse(localStorage.getItem('shikdar_films_admin_activities') || '[]')
    activities.push({
      id: Date.now(),
      type: 'password_reset',
      message: `Password reset for ${email}`,
      timestamp: new Date().toISOString()
    })
    localStorage.setItem('shikdar_films_admin_activities', JSON.stringify(activities))

    toast.success('Password reset successfully! Confirmation email sent.')
    setStep('success')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-bengal-green to-bengal-orange flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-bengal-green to-bengal-orange rounded-2xl flex items-center justify-center shadow-lg">
              <Film className="w-12 h-12 text-white" />
            </div>
          </div>

          {step === 'email' && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 section-title mb-2">
                  Forgot Password?
                </h2>
                <p className="text-gray-600">
                  Enter your email to receive a reset code
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmitEmail)} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
                      })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                      placeholder="admin@shikdarfilms.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-bengal-green hover:bg-bengal-orange text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Send Reset Code
                </button>
              </form>
            </>
          )}

          {step === 'verify' && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 section-title mb-2">
                  Verify Code
                </h2>
                <p className="text-gray-600">
                  Enter the verification code sent to {email}
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmitVerify)} className="space-y-6">
                <div>
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    id="verificationCode"
                    {...register('verificationCode', { required: 'Code is required' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent text-center text-2xl tracking-widest"
                    placeholder="XXXXXX"
                  />
                  {errors.verificationCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.verificationCode.message}</p>
                  )}
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Demo Code:</strong> {resetToken}
                  </p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-bengal-green hover:bg-bengal-orange text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Verify Code
                </button>
              </form>
            </>
          )}

          {step === 'reset' && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 section-title mb-2">
                  Reset Password
                </h2>
                <p className="text-gray-600">
                  Enter your new password
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmitReset)} className="space-y-6">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      id="newPassword"
                      {...register('newPassword', { 
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Password must be at least 8 characters' }
                      })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                      placeholder="Enter new password"
                    />
                  </div>
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      id="confirmPassword"
                      {...register('confirmPassword', { 
                        required: 'Please confirm password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                      placeholder="Confirm new password"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-bengal-green hover:bg-bengal-orange text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Reset Password
                </button>
              </form>
            </>
          )}

          {step === 'success' && (
            <>
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 section-title mb-2">
                  Password Reset!
                </h2>
                <p className="text-gray-600">
                  Your password has been successfully reset
                </p>
              </div>

              <Link
                to="/admin/login"
                className="block w-full bg-bengal-green hover:bg-bengal-orange text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-center"
              >
                Back to Login
              </Link>
            </>
          )}

          {step !== 'success' && (
            <div className="mt-6 text-center">
              <Link 
                to="/admin/login"
                className="inline-flex items-center gap-2 text-bengal-green hover:text-bengal-orange font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword